import React, { useState, useEffect } from 'react';
import { useOrders } from '../contexts/OrderContext';
import { useAuth } from '../contexts/AuthContext';
import { FaCheckCircle, FaClock, FaTruck, FaEye } from 'react-icons/fa';

const AdminOrders = () => {
  const { getAllOrders, updateOrderStatus, deleteOrder } = useOrders();
  const { isAdmin } = useAuth();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const allOrders = await getAllOrders();
        setOrders(allOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [getAllOrders]);
  
  const filteredOrders = selectedStatus === 'all' 
    ? orders 
    : orders.filter(order => order.status === selectedStatus);

  if (!isAdmin) {
    return (
      <div className="container text-center mt-5">
        <h2>Access Denied</h2>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <FaCheckCircle className="text-success" />;
      case 'pending':
        return <FaClock className="text-warning" />;
      default:
        return <FaTruck className="text-info" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    await updateOrderStatus(orderId, newStatus);
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      await deleteOrder(orderId);
    }
  };

  return (
    <div className="container">
      <h2 className="text-center mb-4">Manage Orders</h2>
      
      {/* Status Filter */}
      <div className="mb-4">
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          {['all', 'pending', 'delivered'].map(status => (
            <button
              key={status}
              className={`btn ${selectedStatus === status ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setSelectedStatus(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)} Orders
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center mt-5">
          <h3>No orders found</h3>
        </div>
      ) : (
        <div>
          {filteredOrders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div>
                  <span className="order-id">{order._id}</span>
                  <div className="text-muted">
                    {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {getStatusIcon(order.status)}
                  <span className={`order-status ${order.status}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>
              </div>

              <div className="order-items">
                {order.items.map((item) => (
                  <div key={item.productId} className="order-item">
                    <span>{item.productName} x {item.quantity}</span>
                    <span>₹{item.totalPrice.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="order-item">
                <span>Subtotal:</span>
                                  <span>₹{order.subtotal.toFixed(2)}</span>
              </div>
              
              <div className="order-item">
                <span>Delivery Fee:</span>
                                  <span>₹{order.deliveryFee.toFixed(2)}</span>
              </div>

              <div className="order-item">
                <strong>Total:</strong>
                <strong className="order-total">₹{order.total.toFixed(2)}</strong>
              </div>

              <div className="mt-3">
                <h6>Customer Information:</h6>
                <p className="mb-1"><strong>Name:</strong> {order.userName}</p>
                <p className="mb-1"><strong>Phone:</strong> {order.userPhone}</p>
                <p className="mb-1"><strong>Society:</strong> {order.userSociety}</p>
                <p className="mb-0"><strong>Address:</strong> {order.userAddress}</p>
              </div>

              <div className="mt-3 d-flex gap-2 flex-wrap">
                {order.status === 'pending' && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, 'delivered')}
                    className="btn btn-success btn-sm"
                  >
                    <FaCheckCircle className="me-1" />
                    Mark as Delivered
                  </button>
                )}
                
                {order.status === 'delivered' && (
                  <button
                    onClick={() => handleStatusUpdate(order._id, 'pending')}
                    className="btn btn-warning btn-sm"
                  >
                    <FaClock className="me-1" />
                    Mark as Pending
                  </button>
                )}
                
                <button
                  onClick={() => setSelectedOrder(selectedOrder === order._id ? null : order._id)}
                  className="btn btn-info btn-sm"
                >
                  <FaEye className="me-1" />
                  {selectedOrder === order._id ? 'Hide Details' : 'View Details'}
                </button>
                
                <button
                  onClick={() => handleDeleteOrder(order._id)}
                  className="btn btn-danger btn-sm"
                >
                  Delete Order
                </button>
              </div>

              {selectedOrder === order._id && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6>Payment Information:</h6>
                  <p className="mb-1"><strong>Method:</strong> {order.paymentMethod}</p>
                  <p className="mb-1"><strong>Status:</strong> {order.paymentStatus}</p>
                  <p className="mb-1"><strong>Delivery Date:</strong> {new Date(order.deliveryDate).toLocaleDateString('en-GB')}</p>
                  {order.deliveryInstructions && (
                    <p className="mb-1"><strong>Instructions:</strong> {order.deliveryInstructions}</p>
                  )}
                  {order.notes && (
                    <p className="mb-0"><strong>Notes:</strong> {order.notes}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOrders; 